import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Converts a date-only value to noon UTC to avoid timezone shifting issues.
 *
 * Problem: When a user selects "Nov 5, 2025" as a service date, if we send
 * "2025-11-05T00:00:00.000Z" (midnight UTC), it may display as "Nov 4" in
 * PST timezone (UTC-8), because midnight UTC = 4pm PST the previous day.
 *
 * Solution: Send dates as noon UTC (12:00:00Z). This ensures the date stays
 * the same in all timezones between UTC-12 and UTC+12.
 *
 * @param date - The date to convert (Date, Dayjs, or string)
 * @returns ISO string with time set to 12:00:00.000Z
 */
export function convertDateToNoonUTC(date: Date | Dayjs | string | null | undefined): string | undefined {
  if (!date) return undefined;

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return undefined;

  // Set time to noon UTC (12:00:00.000Z)
  return dayjsDate
    .utc()
    .hour(12)
    .minute(0)
    .second(0)
    .millisecond(0)
    .toISOString();
}

/**
 * Converts a datetime value from local timezone to UTC.
 * Used for time fields (startDatetime, endDatetime) where the time matters.
 *
 * @param datetime - The datetime to convert
 * @returns ISO string in UTC
 */
export function convertDateTimeToUTC(datetime: Date | Dayjs | string | null | undefined): string | undefined {
  if (!datetime) return undefined;

  const dayjsDate = dayjs(datetime);
  if (!dayjsDate.isValid()) return undefined;

  // Convert local time to UTC
  return dayjsDate.utc().toISOString();
}

/**
 * Combines a service date with a time value.
 *
 * @param serviceDate - The date from the service date picker
 * @param timeValue - The time from the time picker or quick select
 * @returns Combined datetime as ISO string in UTC
 */
export function combineDateAndTime(
  serviceDate: Date | Dayjs | string | null | undefined,
  timeValue: Date | Dayjs | string | null | undefined
): string | undefined {
  if (!serviceDate || !timeValue) return undefined;

  const date = dayjs(serviceDate);
  const time = dayjs(timeValue);

  if (!date.isValid() || !time.isValid()) return undefined;

  // Combine the date from serviceDate with the time from timeValue
  const combined = date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0);

  // Convert to UTC
  return combined.utc().toISOString();
}

/**
 * Combines date and time with a backstop for late times (after 7 PM).
 * If the UTC conversion would push the date to the next day, caps the time
 * to prevent timezone-related date mismatches.
 *
 * @param serviceDate - The selected service date
 * @param timeValue - The selected time
 * @returns Combined datetime as ISO string in UTC, with backstop applied
 */
export function combineDateAndTimeWithBackstop(
  serviceDate: Date | Dayjs | string | null | undefined,
  timeValue: Date | Dayjs | string | null | undefined
): string | undefined {
  if (!serviceDate || !timeValue) return undefined;

  const date = dayjs(serviceDate);
  const time = dayjs(timeValue);

  if (!date.isValid() || !time.isValid()) return undefined;

  // Combine the date from serviceDate with the time from timeValue
  const combined = date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0);

  // Get the intended local date (what the user selected)
  const intendedLocalDate = date.format('YYYY-MM-DD');

  // Convert to UTC and check if the date changed
  const utcDatetime = combined.utc();
  const utcDate = utcDatetime.format('YYYY-MM-DD');

  // Backstop: if UTC date is different from intended local date (late time causing rollover),
  // cap to 11:59 PM UTC on the intended date to keep it on the same calendar day
  if (intendedLocalDate !== utcDate) {
    // The time was late enough that UTC conversion pushed it to the next day
    // Cap to 23:59:59 UTC on the intended date
    const capped = dayjs.utc(intendedLocalDate).hour(23).minute(59).second(59).millisecond(0);
    return capped.toISOString();
  }

  return utcDatetime.toISOString();
}

/**
 * Prepares service ticket dates for API submission.
 * IMPORTANT: Both serviceDatetime and startDatetime are set to the SAME combined datetime.
 * This ensures calendars and lists display consistently regardless of which field they read.
 *
 * Also includes a backstop for late times (after 7 PM) to prevent timezone shifts
 * from pushing jobs to the next calendar day.
 *
 * @param values - Form values from the service ticket form
 * @returns Transformed values ready for API submission
 */
export function prepareServiceTicketDatesForAPI(values: any): any {
  const transformed = { ...values };

  // Handle dueDate separately (date-only field)
  if (transformed.dueDate) {
    transformed.dueDate = convertDateToNoonUTC(transformed.dueDate);
  }

  // Combine serviceDatetime and startDatetime into one consistent value
  // Both fields should always be the same to prevent ordering/display issues
  if (transformed.serviceDatetime) {
    const serviceDate = dayjs(transformed.serviceDatetime);

    if (transformed.startDatetime) {
      // Combine date from serviceDatetime with time from startDatetime
      // Apply backstop to prevent late times from rolling to next day
      const combinedDatetime = combineDateAndTimeWithBackstop(
        transformed.serviceDatetime,
        transformed.startDatetime
      );

      // Set BOTH fields to the same combined datetime
      transformed.serviceDatetime = combinedDatetime;
      transformed.startDatetime = combinedDatetime;
    } else {
      // No start time specified - use noon UTC for the date (maintains backward compat)
      const noonUtc = convertDateToNoonUTC(transformed.serviceDatetime);
      transformed.serviceDatetime = noonUtc;
      // Also set startDatetime to the same value for consistency
      transformed.startDatetime = noonUtc;
    }
  }

  // Handle endDatetime - combine with service date if provided
  if (transformed.endDatetime && transformed.serviceDatetime) {
    const endTime = dayjs(transformed.endDatetime);
    const serviceDate = dayjs(transformed.serviceDatetime);

    if (endTime.format('YYYY-MM-DD') !== serviceDate.format('YYYY-MM-DD')) {
      transformed.endDatetime = combineDateAndTimeWithBackstop(
        transformed.serviceDatetime,
        transformed.endDatetime
      );
    } else {
      transformed.endDatetime = convertDateTimeToUTC(transformed.endDatetime);
    }
  }

  // Other datetime fields - just convert to UTC
  if (transformed.timeIn) {
    transformed.timeIn = convertDateTimeToUTC(transformed.timeIn);
  }

  if (transformed.timeOut) {
    transformed.timeOut = convertDateTimeToUTC(transformed.timeOut);
  }

  if (transformed.completedAt) {
    transformed.completedAt = convertDateTimeToUTC(transformed.completedAt);
  }

  return transformed;
}

/**
 * Displays a date from the API in the user's local timezone.
 *
 * @param date - Date from API (should be in UTC)
 * @param format - Optional format string (default: "MMM D, YYYY")
 * @returns Formatted date string in local timezone
 */
export function displayDate(
  date: Date | Dayjs | string | null | undefined,
  format: string = "MMM D, YYYY"
): string {
  if (!date) return "";

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "Invalid date";

  // Display in local timezone
  return dayjsDate.local().format(format);
}

/**
 * Displays a datetime from the API in the user's local timezone.
 *
 * @param datetime - Datetime from API (should be in UTC)
 * @param format - Optional format string (default: "MMM D, YYYY h:mm A")
 * @returns Formatted datetime string in local timezone
 */
export function displayDateTime(
  datetime: Date | Dayjs | string | null | undefined,
  format: string = "MMM D, YYYY h:mm A"
): string {
  if (!datetime) return "";

  const dayjsDate = dayjs(datetime);
  if (!dayjsDate.isValid()) return "Invalid datetime";

  // Display in local timezone
  return dayjsDate.local().format(format);
}
